<script>
  import BaseAnswer from '@assessment/shared/BaseAnswers';
  import { mapState, mapGetters } from 'vuex';

  export default {
    name: 'InboxAnswers',
    data() {
      return {
        selectedAnswers: [],
      };
    },
    props: {
      currentEmail: {
        type: Object,
        required: true,
      },
      info: {
        type: Object,
      },
    },
    components: {
      BaseAnswer,
    },
    mounted() {
      // This runs after the component has been mounted to the DOM
      if (this.currentEmail.FK_questionDisplayTypeKey === 3) {
        const originalEmail = Object.values(this.answeredEmails).filter(
          (email) => email.feedbackKey === this.currentEmail.questionKey
        )[0];
        this.selectedAnswers = originalEmail.answer.map((a) => a.nameTagKey);

        this.pickedAnswers = this.getFeedbackAnswerKey();
      }
    },
    computed: {
      ...mapState(['theme']),
      ...mapGetters('email', ['answeredEmails']),

      primaryButtonColor() {
        const { isPreset } = this.theme;
        return isPreset === 0
          ? { backgroundColor: this.theme.props[3] }
          : { backgroundColor: '#337ab7' };
      },
    },
    methods: {
      getFeedbackAnswerKey() {
        const feedbackAnswerKey = Object.keys(this.currentEmail.answers).filter((answerKey) => {
          return this.currentEmail.answers[answerKey].answerTypeKey === 3;
        })[0];

        return this.currentEmail.answers[feedbackAnswerKey].answerKey;
      },
    },
  };
</script>
<template>
  <base-answer :current-email="currentEmail">
    <!-- watcher needs to watch all current emails to cache written responses -->
    <div
      class="row pl-20 pr-20 pb-20"
      slot-scope="{
        submit,
        isComplete,
        isWrittenResponse,
        answers,
        pickedAnswers,
        submittedAnswers,
        submittedSmartAnswer,
        hasValidAnswer,
        answerProps,
        answerEvents,
      }"
    >
      <hr />
      <template v-if="isComplete && currentEmail.FK_questionDisplayTypeKey !== 3">
        <div class="col-md-12 well questions mb-0">
          <strong>Me:</strong>
          <template v-if="isWrittenResponse">
            <p v-html="currentEmail.writtenResponse"><br /></p>
          </template>
          <template v-else-if="submittedSmartAnswer">
            <p>{{ submittedSmartAnswer }}<br /></p>
          </template>
          <template v-else>
            <span v-for="(answer, i) in submittedAnswers" :key="`answer-${i}`" v-html="answer" />
          </template>
        </div>
      </template>
      <template v-else-if="!isComplete">
        <div class="col-md-12 well questions mb-0">
          <div
            :class="
              isWrittenResponse
                ? 'content-center'
                : currentEmail.FK_questionDisplayTypeKey !== 3
                ? 'col-md-105'
                : ''
            "
          >
            <!-- Written Response question -->
            <template v-if="isWrittenResponse">
              <div class="p-15">
                <wysiwyg
                  :unique-id="currentEmail.questionKey"
                  id="written-response"
                  :is-written-response="true"
                  data-testid="written-response-textbox"
                  v-bind="answerProps.written"
                  v-on="answerEvents.written"
                  class="emailMsgAnswer"
                />
              </div>
            </template>
            <!-- Multi answer question -->
            <template v-else-if="currentEmail.FK_questionTypeKey === 2">
              <span>
                <b><i>Choose all responses that apply.</i></b>
              </span>
              <template v-for="answer in answers">
                <div
                  class="radio"
                  :key="answer.answerKey"
                  v-if="currentEmail.FK_questionDisplayTypeKey !== 3"
                >
                  <label :for="`f-option-${answer.answerKey}`" class="multi-answer-label">
                    <input
                      name="answer"
                      :id="`f-option-${answer.answerKey}`"
                      type="checkbox"
                      :value="answer.answerKey"
                      :checked="pickedAnswers.includes(answer.answerKey)"
                      v-on="answerEvents.multi"
                      :disabled="
                        currentEmail.maxAnswerCount > 1 &&
                        currentEmail.maxAnswerCount <= pickedAnswers.length &&
                        !pickedAnswers.includes(answer.answerKey)
                      "
                    />
                    <span v-html="answer.nameTagKey" class="ml-5"></span>
                  </label>
                </div>
              </template>
            </template>
            <!-- Single answer question -->
            <template
              v-else-if="
                currentEmail.FK_questionTypeKey === 1 &&
                currentEmail.FK_questionDisplayTypeKey !== 3
              "
            >
              <template v-for="answer in answers">
                <div
                  class="radio"
                  :key="answer.answerKey"
                  v-if="currentEmail.FK_questionDisplayTypeKey !== 3"
                >
                  <label :for="`f-option-${answer.answerKey}`">
                    <input
                      :name="`answer`"
                      :id="`f-option-${answer.answerKey}`"
                      type="radio"
                      :value="answer.answerKey"
                      v-on="answerEvents.single"
                      :checked="pickedAnswers === answer.answerKey"
                    />
                    <span v-html="answer.nameTagKey"></span>
                  </label>
                </div>
              </template>
            </template>
            <!-- Feedback question -->
            <template v-else-if="currentEmail.FK_questionDisplayTypeKey === 3">
              <ul>
                <template v-for="answer in answers">
                  <li
                    :key="answer.answerKey"
                    v-if="answer.answerTypeKey !== 3"
                    :class="currentEmail.FK_questionTypeKey === 2 ? 'square-style' : 'circle-style'"
                  >
                    <div
                      :class="{ 'selected-answer': selectedAnswers.includes(answer.nameTagKey) }"
                    >
                      <span
                        v-html="
                          selectedAnswers.includes(answer.nameTagKey)
                            ? answer.nameTagKey.replace(
                                /<p>/,
                                `<p><strong>(Your Response)</strong> &nbsp`
                              )
                            : answer.nameTagKey
                        "
                      ></span>
                    </div>
                    <div
                      v-if="answer.feedback.feedbackText"
                      :style="`background-color: ${answer.feedback.color}; padding: 10px 5px 5px 5px; margin-top:5px; margin-bottom:10px`"
                    >
                      <span
                        v-html="
                          answer.feedback.feedbackText.replace(
                            /<p>/,
                            `<p><strong>Feedback:</strong> &nbsp`
                          )
                        "
                      ></span>
                    </div>
                  </li>
                </template>
              </ul>
            </template>
          </div>

          <div class="col-md-12">
            <button
              @click="submit"
              class="btn btn-primary mat"
              :style="primaryButtonColor"
              :disabled="!hasValidAnswer"
            >
              {{ currentEmail.FK_questionDisplayTypeKey === 3 ? 'Continue' : 'Respond' }}
            </button>
          </div>
        </div>
      </template>
    </div>
  </base-answer>
</template>

<style lang="scss" scope>
  .multi-answer-label {
    display: flex;
    margin-left: -20px;
  }
  input[type='checkbox'] {
    margin-top: -8px;
  }
  .selected-answer {
    border: 2px solid #007bff;
    font-weight: bold;
    padding: 4px;
    border-radius: 4px;
  }
</style>
